import { toast } from 'react-hot-toast'

import { ApolloClient, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'

import { readGaIds, refreshGaIds } from '@web-app/lib/analytics/gaIds'
import { isOffline } from '@web-app/lib/errors/transportFailure'
import type {
  BrowserFingerprint,
  SessionId,
  Token,
} from '@web-app/lib/ironSession'

import { buildCache } from './cache'
import { makeErrorToastLink } from './errorToastLink'
import { browserParser, buildClientInfoHeaders } from './headers'
import {
  conditionalFetchOptionsLink,
  errorLink,
  httpLink,
  makeAuthErrorLink,
} from './links'
import { createTokenMachine } from './tokenMachine'
import type { MintResult } from './tokenMachine'

// The cookie is already destroyed: a protected page reloads into the sign-in
// redirect, an open one into a signed-out render.
const reloadToSignIn = (): void => {
  window.location.reload()
}

// Stateless, so one instance serves every client this module builds. Keyed
// by message: a page whose queries all fail together shows one pill.
const errorToastLink = makeErrorToastLink({
  toast: (message) => toast.error(message, { id: message }),
  isOffline,
})

export type SessionRefs = {
  browserFingerprint?: BrowserFingerprint
  sessionId?: SessionId
  mcid?: string
  userId?: string
}

// Token and mint state live in a machine owned by this client: an identity
// swap discards them WITH the cache, so a credential can never outlive its
// user. `session` is shared by reference and must be read live (never
// snapshotted) — ref updates reach the next operation without a rebuild.
// Readonly: the wrapper is the only writer.
const buildAuthedClient = (session: Readonly<SessionRefs>) => {
  const tokens = createTokenMachine({
    getUserId: () => session.userId,
    reload: reloadToSignIn,
  })

  const authLink = setContext(async (operation, previousContext) => {
    const { headers = {} } = previousContext

    // Asked before the token gate; whatever the tag has answered by the time
    // the headers are built rides this operation. A page's first operations
    // may go without.
    refreshGaIds()

    // The gate: expiring tokens never reach the wire — Apollo holds the
    // operation until this context promise resolves.
    await tokens.ensureFreshToken()

    const token = tokens.getToken()
    const authHeaders = token
      ? { Authorization: `Bearer ${token.replace('Bearer ', '')}` }
      : {}
    const gaIds = readGaIds()

    return {
      // makeAuthErrorLink: a rejected token is worth minting over;
      // tokenless is not.
      hadToken: Boolean(authHeaders.Authorization),
      headers: {
        ...headers,
        ...authHeaders,
        ...buildClientInfoHeaders({
          uaParser: browserParser(),
          browserFingerprint: session.browserFingerprint,
          sessionId: session.sessionId,
          mcid: session.mcid,
          currentUrl: window.location.href,
          userAgent: window.navigator.userAgent,
          gaClientId: gaIds.clientId,
          gaSessionId: gaIds.sessionId,
        }),
      },
    }
  })

  const authErrorLink = makeAuthErrorLink({
    forceMint: tokens.forceMintToken,
    getToken: tokens.getToken,
    reload: reloadToSignIn,
  })

  const client = new ApolloClient({
    ssrMode: false,
    // Both observers sit above authErrorLink: a mint-and-retry that succeeds
    // is neither reported nor toasted.
    link: ApolloLink.from([
      authLink,
      errorLink,
      errorToastLink,
      authErrorLink,
      conditionalFetchOptionsLink,
      httpLink,
    ]),
    cache: buildCache(),
  })

  return {
    client,
    getFreshAccessToken: tokens.getFreshAccessToken,
    forceMintToken: tokens.forceMintToken,
  }
}

// The browser's Apollo state as one owned unit: client, identity, refs and
// credential live and die together.
export const createApolloSession = () => {
  const session: SessionRefs = {}
  let current: ReturnType<typeof buildAuthedClient> | null = null

  const syncSession = (next: SessionRefs) => {
    const identityChanged = session.userId !== next.userId

    // Full replace, never a merge: the refs must mirror the current page's
    // sessionData exactly (a stale userId would make logged-out tabs mint).
    session.browserFingerprint = next.browserFingerprint
    session.sessionId = next.sessionId
    session.mcid = next.mcid
    session.userId = next.userId

    // Swap on identity transitions: the old client is orphaned whole —
    // cache, token, and in-flight operations can't touch the new user's.
    if (!current || identityChanged) {
      current = buildAuthedClient(session)
    }

    return current.client
  }

  return {
    syncSession,
    // undefined means "no credential right now" — a tokenless flight, not
    // death. Callers only run after _app has synced, but stay safe if not.
    getFreshAccessToken: (): Promise<Token | undefined> =>
      current ? current.getFreshAccessToken() : Promise.resolve(undefined),
    forceMintToken: (): Promise<MintResult> =>
      current
        ? current.forceMintToken()
        : Promise.resolve('unavailable' as MintResult),
  }
}
